import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Autocomplete, TextField, Chip, CircularProgress, Box } from '@mui/material';
import { searchUsers, User, UserApiError } from '../../services/userApi';

// Simple debounce function
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function debounce<T extends (...args: any[]) => any>(func: T, wait: number): T {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((...args: any[]) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  }) as T;
}

interface UserSelectorProps {
  value: User[];
  onChange: (users: User[]) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  autoFocus?: boolean;
  /** Sprint D2: When set, exclude users already in this project (for invite-to-project search). */
  excludeProjectId?: number | null;
}

const UserSelector: React.FC<UserSelectorProps> = ({
  value = [],
  onChange,
  onBlur,
  error = false,
  helperText,
  placeholder = 'Search users...',
  disabled = false,
  multiple = true,
  autoFocus = false,
  excludeProjectId = null,
}) => {
  const [options, setOptions] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const debouncedSearch = useCallback(
    debounce(async (query: string, projectId: number | null | undefined) => {
      if (!query || query.length < 2) {
        setOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await searchUsers(query, 10, projectId);
        setOptions(response.users.filter(user => user.isActive));
      } catch (err) {
        console.error('Error searching users:', err);
        setOptions([]);
        if (err && typeof err === 'object' && 'code' in err && (err as UserApiError).code === 'AUTH_REQUIRED') {
          // Silently fail - user will see empty results
        }
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    if (inputValue) {
      debouncedSearch(inputValue, excludeProjectId);
    } else {
      setOptions([]);
    }
  }, [inputValue, excludeProjectId, debouncedSearch]);

  const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  // Track if we're currently selecting an option to prevent premature onBlur
  const isSelectingRef = useRef(false);

  const handleChange = (_event: React.SyntheticEvent, newValue: User | User[] | null) => {
    isSelectingRef.current = true;
    const users = multiple ? ((newValue as User[]) || []) : (newValue ? [newValue as User] : []);
    onChange(users);
    // Reset the flag after a short delay to allow the selection to complete
    // Don't trigger onBlur from here - let the native blur event handle it
    // The parent component will handle the save logic with proper timing
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 100);
  };

  const handleBlur = () => {
    // Don't trigger onBlur if we're in the middle of selecting an option
    // The handleChange will trigger onBlur after selection completes
    if (isSelectingRef.current) {
      return;
    }
    if (onBlur) {
      onBlur();
    }
  };

  return (
    <Autocomplete
      multiple={multiple}
      options={options}
      value={multiple ? value : (value.length > 0 ? value[0] : null)}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onBlur={handleBlur}
      loading={loading}
      disabled={disabled}
      autoFocus={autoFocus}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.displayName || option.email;
      }}
      isOptionEqualToValue={(option, val) => option.userId === val.userId}
      filterOptions={(x) => x} // Disable client-side filtering, we use server-side search
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.userId}
            label={option.displayName || option.email}
            size="small"
          />
        ))
      }
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.userId}>
          <Box>
            <Box sx={{ fontWeight: 500 }}>{option.displayName || option.email}</Box>
            {option.displayName && (
              <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{option.email}</Box>
            )}
          </Box>
        </Box>
      )}
      noOptionsText={inputValue.length < 2 ? 'Type at least 2 characters to search' : 'No users found'}
    />
  );
};

export default UserSelector;

